import { useState, useEffect } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { API } from "./helpers";

export function CreateCustomerDialog({ open, onClose, onSaved, initialData }) {
  const [form, setForm] = useState({
    firma: "", vorname: "", nachname: "", position: "",
    telefon: "", mobil: "", email: "", webseite: "",
    strasse: "", plz: "", ort: "", card_image_path: null,
  });
  const [saving, setSaving] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm((prev) => ({
        ...prev,
        ...initialData,
        strasse: initialData.strasse || initialData.adresse || "",
      }));
      // If initialData has fields beyond firma, show optional section
      if (initialData.vorname || initialData.nachname || initialData.email) {
        setShowOptional(true);
      }
    }
  }, [initialData]);

  const save = async () => {
    if (!form.firma.trim()) {
      toast.error("Bitte Firma angeben");
      return;
    }
    setSaving(true);
    const res = await authFetch(`${API}/crm/contacts`, { method: "POST", body: JSON.stringify(form) });
    if (res.ok) {
      const data = await res.json();
      toast.success("Kunde erstellt");
      onSaved(data.id);
      onClose();
      setForm({ firma: "", vorname: "", nachname: "", position: "", telefon: "", mobil: "", email: "", webseite: "", strasse: "", plz: "", ort: "", card_image_path: null });
      setShowOptional(false);
    }
    setSaving(false);
  };

  const optionalFields = [
    { key: "vorname", label: "Vorname" }, { key: "nachname", label: "Nachname" },
    { key: "position", label: "Position" }, { key: "telefon", label: "Telefon" },
    { key: "mobil", label: "Mobil" }, { key: "email", label: "E-Mail" },
    { key: "webseite", label: "Webseite", span: 2 },
    { key: "strasse", label: "Strasse", span: 2 }, { key: "plz", label: "PLZ" },
    { key: "ort", label: "Ort" },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Neuer Kunde</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Required: Firma */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Firma *</Label>
            <Input
              value={form.firma}
              onChange={(e) => setForm({ ...form, firma: e.target.value })}
              placeholder="Firmenname eingeben"
              className="h-9 text-[13px] mt-1"
              data-testid="new-contact-firma"
              autoFocus
            />
          </div>

          {/* Hint */}
          <p className="text-[11px] text-gray-400 bg-gray-50 rounded-md px-3 py-2" data-testid="quick-create-hint">
            Weitere Details koennen spaeter im CRM ergaenzt werden.
          </p>

          {/* Toggle optional fields */}
          <button
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            data-testid="toggle-optional-fields"
          >
            {showOptional ? "Optionale Felder ausblenden" : "Optionale Felder anzeigen"}
          </button>

          {/* Optional fields */}
          {showOptional && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-1 border-t border-gray-100">
              {optionalFields.map((f) => (
                <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                  <Label className="text-[10px] uppercase tracking-wider text-gray-400">{f.label}</Label>
                  <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="h-8 text-[12px]" data-testid={`new-contact-${f.key}`} />
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="save-new-contact-btn">{saving ? "Speichert..." : "Erstellen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
